/**
 * Plan and seats (Figma 09 · Plan and seats 01–08). Not built, no backend
 * yet: Request upgrade, and "The plans" comparison (the presets live in the
 * backend's OrgPlan.ts and are not exposed).
 */
import { ArrowRight, BadgeCheck, Hourglass, Plus } from "lucide-react";
import { Link } from "react-router";
import { cn } from "@/utils/helpers";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOrgSystemBudget } from "@/hooks/useOrgSystemBudget";
import {
  useOrganizationInvites,
  useOrganizationMembers,
} from "@/app/routes/dashboard/settings/api/useOrganization";
import type { PaymentPlan } from "@/types/organization.type";
import { ErrorState } from "../../ViewStates";
import {
  Card,
  CardLoading,
  DetailHead,
  Fact,
  Facts,
  Note,
  PageHead,
  PersonMark,
  QuietNote,
  SectionHead,
  SettingsPage,
  StatusLabel,
  TextAction,
} from "../parts/SettingsParts";
import { useWorkspaceHead } from "../parts/scope";
import { formatDay, formatUsd, monthName, plural } from "../parts/settingsFormat";

export function planName(plan: PaymentPlan | string | null | undefined): string {
  switch (plan) {
    case "SOLO":
      return "Solo";
    case "TEAM":
      return "Team";
    case "MAX":
      return "Max";
    default:
      return "Custom";
  }
}

/** Settings / Seat (Figma 7:3116): a 48 disc over a name and one line, columns of equal width. */
function SeatTile({
  lead,
  title,
  sub,
  to,
  muted,
}: {
  lead: React.ReactNode;
  title: string;
  sub: string;
  to?: string;
  /** Free seats: both lines muted. */
  muted?: boolean;
}) {
  const body = (
    <>
      {lead}
      <span className={cn("flex w-full flex-col items-center gap-0.5 text-center", muted && "text-co-muted")}>
        <span className={cn("w-full truncate text-[13px] font-semibold leading-[18px]", !muted && "text-co-text")}>{title}</span>
        <span className={cn("w-full truncate text-[12px] leading-4", !muted && "text-co-muted")}>{sub}</span>
      </span>
    </>
  );
  const className = "flex min-w-[96px] flex-1 basis-0 flex-col items-center gap-2";
  return to ? (
    <Link to={to} className={cn("unstyled-link", className)}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

function GlyphDisc({ icon: Glyph, outlined }: { icon: typeof Plus; outlined?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-12 shrink-0 place-items-center rounded-full",
        outlined ? "border border-co-line2 text-co-muted" : "bg-co-tonal text-co-text2",
      )}
    >
      <Glyph className="size-6" />
    </span>
  );
}

export default function PlanPage() {
  const head = useWorkspaceHead();
  const { selectedOrganization, user } = useCurrentUser();
  const org = selectedOrganization?.organization;
  const isOwner = selectedOrganization?.role === "Owner";
  const budget = useOrgSystemBudget();
  const members = useOrganizationMembers(org?.id ?? null);
  const invites = useOrganizationInvites(org?.id ?? null, isOwner);

  const isLoading = budget.isLoading || members.isLoading;
  const isError = budget.isError || members.isError;

  if (isLoading || isError || !org) {
    return (
      <SettingsPage>
        <PageHead {...head} title="Plan and seats" status={isLoading ? "Reading the plan." : undefined} />
        {isLoading ? (
          <CardLoading caption="Loading the plan" />
        ) : (
          <ErrorState
            title="The plan did not load"
            body="Creo could not read it. Your seats and budgets have not changed."
            onRetry={() => {
              void budget.refetch();
              void members.refetch();
            }}
          />
        )}
      </SettingsPage>
    );
  }

  const team = (members.data ?? []).filter((m) => m.role !== "Guest");
  const pendingTeam = (invites.data ?? []).filter((i) => i.role !== "Guest");
  const seats = budget.data?.seats;
  const maxSeats = seats?.maxSeats ?? org.maxSeats ?? null;
  const used = seats ? seats.members + seats.pendingInvites : team.length + pendingTeam.length;
  const free = maxSeats != null ? Math.max(0, maxSeats - used) : 0;
  const full = maxSeats != null && used >= maxSeats;
  const poolUsd = budget.data?.limitUsd ?? org.monthlyAiBudgetUsd ?? null;
  const perSeat = org.perSeatBudgetUsd ?? null;
  const plan = planName(budget.data?.plan ?? org.paymentPlan);

  const summary = [
    maxSeats != null ? plural(maxSeats, "seat") : null,
    perSeat != null ? `${formatUsd(perSeat)} a person each month` : null,
    poolUsd != null ? `${formatUsd(poolUsd)} workspace pool` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const status = full
    ? `All ${maxSeats} seats are taken. Clients do not take a seat.`
    : `What ${org.name} has from Mohtawa: its seats and its monthly budgets.`;

  return (
    <SettingsPage>
      <PageHead {...head} title="Plan and seats" status={status} />
      <Card>
        <DetailHead
          lead={<PersonMark name={plan} size={56} />}
          title={`${plan} plan`}
          sub={summary || undefined}
          aside={<StatusLabel icon={BadgeCheck}>Set by Mohtawa</StatusLabel>}
        />

        <SectionHead title="Seats" meta={maxSeats != null ? `${used} of ${maxSeats} in use` : `${used} in use`} />
        <div className="flex w-full flex-wrap gap-3">
          {team.map((m) => (
            <SeatTile
              key={m.userId}
              lead={<PersonMark name={m.name || m.email} picture={m.profilePicture} size={48} />}
              title={m.name || m.email}
              sub={`${m.role}${m.userId === user?.id ? " · You" : ""}`}
            />
          ))}
          {pendingTeam.map((i) => (
            <SeatTile key={i.id} lead={<GlyphDisc icon={Hourglass} />} title={i.email} sub={`Invited · until ${formatDay(i.expiresAt)}`} />
          ))}
          {Array.from({ length: Math.min(free, 8) }, (_, i) => (
            <SeatTile
              key={`free-${i}`}
              lead={<GlyphDisc icon={Plus} outlined />}
              muted
              title="Free"
              sub={isOwner ? "Invite someone" : "Open"}
              to={isOwner ? ".?page=people&invite=1" : undefined}
            />
          ))}
        </div>
        {maxSeats === 1 ? (
          <QuietNote>
            Solo has one seat, yours. To bring a teammate in, ask Mohtawa for more seats. Clients do not take a seat, so
            you can still invite clients.
          </QuietNote>
        ) : full ? (
          <Note tone="alert">
            All {maxSeats} seats are taken{pendingTeam.length ? ", some by invitations" : ""}. To invite someone else,
            free a seat from People, or ask Mohtawa for more seats.
          </Note>
        ) : (
          <QuietNote>A seat is held for an invitation until it is answered or ends. Clients do not take a seat.</QuietNote>
        )}

        <SectionHead title="Budgets" meta="Reset on the 1st" />
        <Facts>
          {perSeat != null ? <Fact wide label="Each person">{formatUsd(perSeat)} of allowance a month</Fact> : null}
          {poolUsd != null ? (
            <Fact wide label="Workspace pool">{formatUsd(poolUsd)} a month, for overnight planning and client reviews</Fact>
          ) : null}
        </Facts>
        {isOwner ? (
          <TextAction icon={ArrowRight} to=".?page=workspace-usage">
            See {monthName(budget.data?.periodStart)}’s usage
          </TextAction>
        ) : null}
        <QuietNote>Mohtawa sets the plan and can set a custom mix of seats and budgets.</QuietNote>
      </Card>
    </SettingsPage>
  );
}
